import React, { useContext, useMemo } from 'react';

import { User_Role, Retailer_Network_User_Role } from '@occc/graphql';

import { Feature } from './types';

// Just in case in the future we will have other scoped roles except Retailer Connect ones
export type UserScopedRole = Retailer_Network_User_Role | null;

export type AccessControlMatrixRow = [User_Role, UserScopedRole, Feature[]];
export type AccessControlMatrix = AccessControlMatrixRow[];

// Validate duplicates in the matrix (no repeated user-roles)
function isAccessControlMatrixValid(matrix: AccessControlMatrix) {
  return (
    new Set(matrix.map(([userRole, userScopedRole]) => `${userRole}:${userScopedRole}`)).size ===
    matrix.length
  );
}

export type AccessControlContextValue = {
  userRole: User_Role;
  userScopedRole: UserScopedRole;
  accessControlMatrix: AccessControlMatrix;
};

export const AccessControlContext = React.createContext<AccessControlContextValue | null>(null);

export const useAccessControlContext = () => {
  return useContext(AccessControlContext);
};

type AccessControlProviderProps = {
  children: React.ReactNode;
  userRole: User_Role | null;
  userScopedRole: UserScopedRole;
  accessControlMatrix: AccessControlMatrix;
};

export const AccessControlProvider = ({
  children,
  userRole,
  userScopedRole,
  accessControlMatrix,
}: AccessControlProviderProps) => {
  if (accessControlMatrix && !isAccessControlMatrixValid(accessControlMatrix)) {
    throw new Error('Development Error. Access Control Matrix invalid');
  }

  const accessControlContextValue = useMemo<AccessControlContextValue | null>(
    () =>
      userRole && accessControlMatrix ? { userRole, userScopedRole, accessControlMatrix } : null,
    [userRole, userScopedRole, accessControlMatrix],
  );

  return (
    <AccessControlContext.Provider value={accessControlContextValue}>
      {children}
    </AccessControlContext.Provider>
  );
};
